import {
  AreaTooltip,
  Box,
  calculatePercentageChange,
  ComparedPeriodType,
  Disclaimer,
  Div,
  FinancesComparedOverviewEntity,
  FinancesOverviewEntity,
  formatAmount,
  formatDateByGroupByTypeChart,
  GroupByType,
  H3,
  H5,
  lightTheme,
  NumberType,
  P,
  renderPercentageChange,
} from '@vgrubs/components';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  currencyYAxisWidth,
  CurrencyYAxisTick,
  CustomXAxisTick,
} from './chartHelpers';

const GRADIENT_ID = 'pnlFill';

/**
 * Net P&L tooltip — shows the computed profitAndLoss for the bucket (green
 * when ≥ 0, red when negative), plus the previous-period value when comparing.
 */
const ProfitAndLossTooltip = ({
  payload,
  label,
  groupByType,
}: {
  payload?: { dataKey: string; value: number }[];
  label?: string;
  groupByType: GroupByType;
}) => {
  if (!payload?.length) return null;
  const valueFor = (key: string) =>
    payload.find((p) => p.dataKey === key)?.value;
  const current = valueFor('profitAndLoss');
  const compare = valueFor('profitAndLossToCompare');
  const usd = (v: number | undefined) => formatAmount(v ?? 0, NumberType.USD);

  return (
    <AreaTooltip>
      <P $m="0" $bold>
        {formatDateByGroupByTypeChart(label ?? '', groupByType, true)}
      </P>
      {current !== undefined && (
        <Div $flex $alignCenter $between $gap="12px">
          <P $m="0">Operating Profit</P>
          <P
            $m="0"
            $bold
            $color={current >= 0 ? lightTheme.green : lightTheme.primary}
          >
            {usd(current)}
          </P>
        </Div>
      )}
      {compare !== undefined && (
        <Div $flex $alignCenter $between $gap="12px">
          <Disclaimer $m="0">Previous</Disclaimer>
          <Disclaimer $m="0" $color={lightTheme.darkGray} $bold>
            {usd(compare)}
          </Disclaimer>
        </Div>
      )}
    </AreaTooltip>
  );
};

/**
 * Net Profit (P&L) card. Renders the single computed `profitAndLoss` series
 * as a primary-style area whose color splits at the zero baseline via a
 * gradient — green above, red below. Fill styling (stroke:none + opacity:0.8)
 * mirrors every other primary single-area chart in the dashboard, so the
 * shade matches Total Income / Operating Expenses / VR Order Loss.
 */
export const ProfitAndLossChart = ({
  data,
  comparedData,
  groupByType,
  periodTypeComparedTo,
}: {
  data?: FinancesOverviewEntity | null;
  comparedData?: FinancesComparedOverviewEntity | null;
  groupByType: GroupByType;
  periodTypeComparedTo?: ComparedPeriodType;
}) => {
  const isComparing =
    periodTypeComparedTo === ComparedPeriodType.LAST_MONTH ||
    periodTypeComparedTo === ComparedPeriodType.LAST_YEAR ||
    periodTypeComparedTo === ComparedPeriodType.CUSTOM;

  // Money stays in cents end-to-end; the Y-axis tick + tooltip divide by 100
  // at render time. Negatives are kept (drives the green→red split).
  const series = isComparing
    ? (comparedData?.profitAndLoss.series || []).map((p) => ({
        key: p.key,
        profitAndLoss: p.value,
        profitAndLossToCompare: p.valueToCompare,
      }))
    : (data?.series.profitAndLoss || []).map((p) => ({
        key: p.key,
        profitAndLoss: p.value,
      }));

  const netSum = isComparing
    ? comparedData?.profitAndLoss.total ?? 0
    : data?.profitAndLoss ?? 0;
  const netSumToCompare = comparedData?.profitAndLoss.totalToCompare ?? 0;

  // Zero-crossing ratio for the gradient: fraction of the y-range (top→bottom)
  // that sits above zero. Everything above the offset is green, below is red.
  const values = series.map((d) => d.profitAndLoss);
  // The compare overlay stretches the axis: `domain` below is only a hint while
  // `allowDataOverflow` stays false, so recharts widens the range to fit this
  // series and prints the tick labels from it.
  const compareValues = isComparing
    ? (comparedData?.profitAndLoss.series || []).map((p) => p.valueToCompare)
    : [];
  const dataMax = values.length ? Math.max(...values) : 0;
  const dataMin = values.length ? Math.min(...values) : 0;
  const gradientOffset =
    dataMax <= 0 ? 0 : dataMin >= 0 ? 1 : dataMax / (dataMax - dataMin);

  const calculatedPercentage = calculatePercentageChange(
    netSum || 0,
    netSumToCompare || 0
  );

  return (
    <Box $p="0" $pSM="0" $relative>
      {/* Header — matches the layout used by the other chart cards. */}
      <Div $p="16px 20px 0 20px">
        <Div $flex $alignCenter $gap="8px">
          <H5 $m="0">Operating Profit</H5>
          {/* Inverted. The compared value is normally negative, and
              calculatePercentageChange divides by the signed baseline, so a
              shrinking loss comes back as a minus. Green on a minus means the
              number moved the right way. */}
          {isComparing && renderPercentageChange(calculatedPercentage, true)}
        </Div>
        <Div $flex $alignCenter $gap="8px">
          <H3
            $mb="0"
            $color={netSum >= 0 ? lightTheme.green : lightTheme.primary}
          >
            {formatAmount(netSum, NumberType.USD)}
          </H3>
          {isComparing && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              vs {formatAmount(netSumToCompare, NumberType.USD)}
            </Disclaimer>
          )}
        </Div>
      </Div>

      {/* Chart */}
      <Div $width="100%" $height="280px" $heightSM="315px">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={series}
            margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
          >
            <defs>
              {/* Hard split at zero. Both stops at full saturation — the
                  Area's opacity prop applies the 0.8 fade, identical to how
                  ReusableChart renders every other primary single-area chart. */}
              <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                <stop offset={gradientOffset} stopColor={lightTheme.green} />
                <stop offset={gradientOffset} stopColor={lightTheme.primary} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 2" />
            <XAxis
              dataKey="key"
              tick={(props) => (
                <CustomXAxisTick {...props} groupByType={groupByType} />
              )}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              // Pin the domain to the data extent so the gradient's
              // zero-crossing offset aligns exactly with the rendered baseline.
              domain={values.length ? [dataMin, dataMax] : undefined}
              tick={CurrencyYAxisTick}
              // A six-figure loss prints "-$115.81K", which is wider than the
              // default 60px axis and spills off the left edge of the card.
              width={currencyYAxisWidth([...values, ...compareValues])}
              tickLine={false}
              axisLine={false}
            />
            <ReferenceLine y={0} stroke={lightTheme.lightGray} />
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none' }}
              content={(p) => (
                <ProfitAndLossTooltip
                  {...(p as any)}
                  groupByType={groupByType}
                />
              )}
            />
            {isComparing && (
              // Compare overlay — same shape ReusableChart uses for the
              // non-primary Area: dashed gray line, faded fill.
              <Area
                type="monotone"
                dataKey="profitAndLossToCompare"
                stroke={lightTheme.darkGray}
                strokeWidth={1}
                strokeDasharray="5 5"
                fill={lightTheme.darkGray}
                fillOpacity={0.3}
              />
            )}
            <Area
              type="monotone"
              dataKey="profitAndLoss"
              stroke="none"
              fill={`url(#${GRADIENT_ID})`}
              fillOpacity={1}
              opacity={0.8}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Div>
    </Box>
  );
};
